import React from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { setData } from 'platform/forms-system/src/js/actions';
import ArrayBuilderCards from 'platform/forms-system/src/js/patterns/array-builder/ArrayBuilderCards';
import { initGetText } from 'platform/forms-system/src/js/patterns/array-builder/helpers';

import { normalizeArrayBuilderRequired } from '../pages/supportingEvidence/utils';
import { vaMedicalRecordsOptions } from '../pages/supportingEvidence/vaMedicalRecords/options';
import { VA_MEDICAL_RECORDS_ITEM_PATH } from '../pages/supportingEvidence/vaMedicalRecordsPages';
import { privateMedicalRecordsOptions } from '../pages/supportingEvidence/privateMedicalRecords/options';
import { PRIVATE_MEDICAL_RECORDS_RELEASE_ITEM_PATH } from '../pages/supportingEvidence/privateMedicalRecordsPages';
import {
  arrayBuilderOptions as pmrUploadOptions,
  PMR_UPLOAD_ITEM_PATH,
} from '../pages/supportingEvidence/privateMedicalRecordsUploadPages';
import {
  arrayBuilderOptions as additionalEvidenceOptions,
  ADDITIONAL_EVIDENCE_ITEM_PATH,
} from '../pages/supportingEvidence/additionalEvidenceUploadPages';
import {
  shaUploadSummaryOptions,
  SHA_UPLOAD_PATH,
} from '../pages/supportingEvidence/separationHealthAssessmentPages';
import {
  serviceTreatmentRecordsOptions as strUploadOptions,
  STR_UPLOAD_ITEM_PATH,
} from '../pages/supportingEvidence/serviceTreatmentRecords/options';

/**
 * Renders an arrayBuilder loop's own cards (via the platform's
 * `ArrayBuilderCards`), reusing that loop's `options` (text, required,
 * isItemIncomplete, etc.) so the cards, edit links, and delete confirmation
 * modal are identical to what the loop renders on its own summary page.
 */
const EvidenceSection = ({
  heading,
  options,
  itemPath,
  formData,
  dispatch,
}) => {
  const items = formData[options.arrayPath] || [];
  if (!items.length) return null;

  const getText = initGetText({
    textOverrides: options.text,
    getItemName: options.text.getItemName,
    nounPlural: options.nounPlural,
    nounSingular: options.nounSingular,
  });

  return (
    <>
      <h4>{heading}</h4>
      <ArrayBuilderCards
        arrayPath={options.arrayPath}
        fullData={formData}
        nounSingular={options.nounSingular}
        isIncomplete={(itemData, _index, fullData) =>
          options.isItemIncomplete(itemData, fullData)
        }
        getEditItemPathUrl={() => itemPath}
        urlPrefix="/"
        getText={getText}
        required={normalizeArrayBuilderRequired(options.required)}
        isReview={false}
        titleHeaderLevel="4"
        onRemove={(_index, _item, newData) => dispatch(setData(newData))}
        onRemoveAll={newData => dispatch(setData(newData))}
      />
    </>
  );
};

EvidenceSection.propTypes = {
  dispatch: PropTypes.func.isRequired,
  formData: PropTypes.object.isRequired,
  heading: PropTypes.string.isRequired,
  itemPath: PropTypes.string.isRequired,
  options: PropTypes.shape({
    arrayPath: PropTypes.string.isRequired,
    nounSingular: PropTypes.string.isRequired,
    nounPlural: PropTypes.string.isRequired,
    required: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
    isItemIncomplete: PropTypes.func.isRequired,
    text: PropTypes.shape({
      getItemName: PropTypes.func.isRequired,
    }).isRequired,
  }).isRequired,
};

/**
 * Summary of multiple types of supporting evidence provided in Step 4:
 * Separation Health Assessment and service treatment records uploads (BDD),
 * VA/military facility records we'll request, provider facility records
 * we'll request, self-uploaded private medical records, and additional
 * supporting documents. Each section reuses its own list & loop's cards via
 * the platform's `ArrayBuilderCards`.
 */
const SupportingEvidenceSummary = () => {
  const dispatch = useDispatch();
  const formData = useSelector(state => state?.form?.data) || {};

  const hasAnyEvidence =
    (formData[vaMedicalRecordsOptions.arrayPath] || []).length ||
    (formData[privateMedicalRecordsOptions.arrayPath] || []).length ||
    (formData[pmrUploadOptions.arrayPath] || []).length ||
    (formData[additionalEvidenceOptions.arrayPath] || []).length ||
    (formData[shaUploadSummaryOptions.arrayPath] || []).length ||
    (formData[strUploadOptions.arrayPath] || []).length;

  return (
    <div>
      {!hasAnyEvidence && (
        <p>
          You haven’t provided any evidence. This may delay us processing your
          claim. In addition, we may also schedule a claim exam for you to help
          us decide your claim.
        </p>
      )}
      <EvidenceSection
        heading="Separation Health Assessment you uploaded"
        options={shaUploadSummaryOptions}
        itemPath={SHA_UPLOAD_PATH}
        formData={formData}
        dispatch={dispatch}
      />
      <EvidenceSection
        heading="Service treatment records you uploaded"
        options={strUploadOptions}
        itemPath={STR_UPLOAD_ITEM_PATH}
        formData={formData}
        dispatch={dispatch}
      />
      <EvidenceSection
        heading="Records we’ll request from VA or military medical facilities"
        options={vaMedicalRecordsOptions}
        itemPath={VA_MEDICAL_RECORDS_ITEM_PATH}
        formData={formData}
        dispatch={dispatch}
      />
      <EvidenceSection
        heading="Records we’ll request from other healthcare providers"
        options={privateMedicalRecordsOptions}
        itemPath={PRIVATE_MEDICAL_RECORDS_RELEASE_ITEM_PATH}
        formData={formData}
        dispatch={dispatch}
      />
      <EvidenceSection
        heading="Records you uploaded"
        options={pmrUploadOptions}
        itemPath={PMR_UPLOAD_ITEM_PATH}
        formData={formData}
        dispatch={dispatch}
      />
      <EvidenceSection
        heading="Additional documents you uploaded"
        options={additionalEvidenceOptions}
        itemPath={ADDITIONAL_EVIDENCE_ITEM_PATH}
        formData={formData}
        dispatch={dispatch}
      />
      <p>Next, we’ll tell you what to expect during a claim exam.</p>
    </div>
  );
};

// Exported for unit tests.
export { SupportingEvidenceSummary };

export default SupportingEvidenceSummary;
